import { Component, OnInit, inject } from '@angular/core';
import { AuthorizeService, AuthenticationResultStatus, AuthFailureCode } from '../authorize.service';
import { authFailureText } from '../auth-messages';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, take } from 'rxjs';
import {
  LoginActions,
  QueryParameterNames,
  ApplicationPaths,
  ReturnUrlType,
} from '../api-authorization.constants';
import { environment } from 'src/environments/environment';
import { AppProfileService } from 'src/app/services/profile.service';
import { CommonModule } from '@angular/common';

// The main responsibility of this component is to handle the user's login process.
// This is the starting point for the login process. Any component that needs to authenticate
// a user can simply perform a redirect to this component with a returnUrl query parameter and
// let the component perform the login and return back to the return url.
@Component({
    imports: [CommonModule],
    selector: 'app-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.css']
})
export class LoginComponent implements OnInit {
  private authorizeService = inject(AuthorizeService);
  private appProfileService = inject(AppProfileService);
  private activatedRoute = inject(ActivatedRoute);
  private router = inject(Router);

  public message = new BehaviorSubject<string>(null);

  async ngOnInit() {
    const action = this.activatedRoute.snapshot.url[1];
    switch (action.path) {
      case LoginActions.Login:
        await this.login(this.getReturnUrl());
        break;
      case LoginActions.LoginCallback:
        await this.processLoginCallback();
        break;
      case LoginActions.LoginFailed:
        // #178: the query string carries a CODE now, not prose. It used to carry whatever the
        // OIDC library threw, which then rendered into the page — and sat in the user's URL.
        this.message.next(
          authFailureText(
            this.activatedRoute.snapshot.queryParamMap.get(
              QueryParameterNames.Message
            ) as AuthFailureCode
          )
        );
        break;
      case LoginActions.Register:
        this.redirectToRegister();
        break;
      case LoginActions.RegisterChildUser:
        this.authorizeService
          .getUser()
          .pipe(take(1))
          .subscribe((u) => {
            this.redirectToRegisterChildUser(u.sub);
          });
        break;
      default:
        throw new Error(`Invalid action '${action}'`);
    }
  }

  private async login(returnUrl: string): Promise<void> {
    const state: INavigationState = { returnUrl };
    const result = await this.authorizeService.signIn(state);
    this.message.next(undefined);
    switch (result.status) {
      case AuthenticationResultStatus.Redirect:
        break;
      case AuthenticationResultStatus.Success:
        await this.navigateToReturnUrl(returnUrl);
        break;
      case AuthenticationResultStatus.Fail:
        await this.router.navigate(ApplicationPaths.LoginFailedPathComponents, {
          queryParams: { [QueryParameterNames.Message]: result.code },
        });
        break;
      default:
        throw new Error(`Invalid status result ${(result as any).status}.`);
    }
  }

  private async processLoginCallback(): Promise<void> {
    const url = window.location.href;
    const result = await this.authorizeService.completeSignIn(url);
    switch (result.status) {
      case AuthenticationResultStatus.Redirect:
        // There should not be any redirects as completeSignIn never redirects.
        throw new Error('Should not redirect.');
      case AuthenticationResultStatus.Success:
        // Decided once, on the profile this refresh produces (#199) — see reloadCurrentProfile.
        this.appProfileService.reloadCurrentProfile().subscribe((profile) => {
          if (profile) {
            // For now do not require 2FA auth
            this.router.navigate([this.getReturnUrl(result.state)]);
          } else {
            this.authorizeService
              .getUser()
              .pipe(take(1))
              .subscribe(() => {
                this.router.navigate([ApplicationPaths.ApplicationProfileWelcomePage]);
              });
          }
        });
        break;
      case AuthenticationResultStatus.Fail:
        console.error(`Sign-in failed (${result.code}): ${result.message}`);
        this.message.next(authFailureText(result.code));
        break;
    }
  }

  private redirectToRegister(): any {
    this.redirectToApiAuthorizationPath(
      `${ApplicationPaths.IdentityRegisterPath}?returnUrl=${encodeURI(
        '/' + ApplicationPaths.Login
      )}`
    );
  }

  private redirectToRegisterChildUser(parentSub: string): any {
    this.redirectToApiAuthorizationPath(
      `${
        ApplicationPaths.IdentityRegisterChildUserPath
      }?returnUrl=${this.getReturnUrl()}&parentSub=${parentSub}`
    );
  }

  private async navigateToReturnUrl(returnUrl: string) {
    // It's important that we do a replace here so that we remove the callback uri with the
    // fragment containing the tokens from the browser history.
    await this.router.navigateByUrl(returnUrl, {
      replaceUrl: true,
    });
  }

  private getReturnUrl(state?: INavigationState): string {
    const fromQuery = (this.activatedRoute.snapshot.queryParams as INavigationState).returnUrl;
    // If the url is comming from the query string, check that is either
    // a relative url or an absolute url
    // TODO: Validate return url
    var returnUrl =
      (state && state.returnUrl) || fromQuery || ApplicationPaths.DefaultLoginRedirectPath;
    return returnUrl;
  }

  private redirectToApiAuthorizationPath(apiAuthorizationPath: string) {
    // It's important that we do a replace here so that when the user hits the back arrow on the
    // browser they get sent back to where it was on the app instead of to an endpoint on this
    // component.
    const redirectUrl = `${environment.identityServerUrl}${apiAuthorizationPath}`;
    window.location.replace(redirectUrl);
  }
}

interface INavigationState {
  [ReturnUrlType]: string;
}
